import { forwardRef, type ReactNode, useId, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { FieldDescription } from "../field/fieldDescription.js";
import { FieldError } from "../field/fieldError.js";
import { Size } from "../helpers/helpers.js";
import { useAutoResizeTextarea } from "../hooks/useAutoResizeTextarea.js";
import { Label } from "../Label/label.js";

export const TextareaVariant = {
  DEFAULT: "default",
  MINIMAL: "minimal",
} as const;
export const allTextareaVariants = [
  TextareaVariant.DEFAULT,
  TextareaVariant.MINIMAL,
] as const;
export type TextareaVariant = (typeof allTextareaVariants)[number];

interface TextareaProps {
  id?: string;
  label?: string;
  labelAccessory?: ReactNode;
  placeholder?: string;
  description?: string | React.ReactNode;
  value: string;
  errorDescription?: string;
  onChange: (newValue: string) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  isDisabled?: boolean;
  autoFocus?: boolean;
  shouldDisableNewline?: boolean; // New prop to optionally disable newline insertion
  minLength?: number;
  maxLength?: number;
  height?: string;
  /**
   * Fill the available vertical space in a flex parent and scroll internally
   * when content exceeds it. Disables the content-driven auto-resize.
   * Mutually exclusive with `height`; takes precedence if both are set.
   */
  fillHeight?: boolean;
  leftIcon?: ReactNode;
  className?: string;
  textareaClassName?: string;
  variant?: TextareaVariant;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props, ref) => {
    const {
      id,
      placeholder,
      description,
      value,
      errorDescription,
      onChange,
      onPaste,
      onKeyDown,
      onBlur,
      onFocus,
      isDisabled,
      autoFocus,
      shouldDisableNewline,
      label,
      labelAccessory,
      maxLength,
      minLength,
      height,
      fillHeight,
      leftIcon,
      className,
      variant = TextareaVariant.DEFAULT,
    } = props;

    const textareaInnerWrapperRef = useRef<HTMLDivElement>(null);

    const localTextareaRef = useRef<HTMLTextAreaElement>(null);
    // Use provided ref if available, otherwise use local ref
    const textareaRef =
      (ref as React.RefObject<HTMLTextAreaElement>) || localTextareaRef;

    const hasError = Boolean(errorDescription);
    // Fall back to a generated id so two textareas rendered without one can't
    // end up describing each other.
    const generatedId = useId();
    const descriptionId = `${id ?? generatedId}-description`;
    const descriptionText = errorDescription ?? description;

    // Automatically expand the textarea to fit content height
    const shouldAutoAdjustHeight = height === undefined && !fillHeight;
    useAutoResizeTextarea({
      textareaRef,
      wrapperRef: textareaInnerWrapperRef,
      value,
      isEnabled: shouldAutoAdjustHeight,
    });

    const textColor = hasError ? "text-red-900" : "text-foreground";
    const ringColor = hasError ? "ring-red-300" : "ring-border-strong";
    const placeholderColor = hasError
      ? "placeholder-red-400"
      : "placeholder-gray-400";

    const wrapperFocusStyle = hasError
      ? "focus-within:ring-red-500"
      : "focus-within:ring-indigo-600";

    const getBackgroundColor = (): string => {
      if (isDisabled) return "bg-slate-50";
      if (variant === TextareaVariant.MINIMAL) return "";
      return "bg-white";
    };

    const textareaWrapperClassNames = twMerge(
      "group relative block whitespace-pre-wrap",
      variant === TextareaVariant.MINIMAL
        ? "p-0 ring-0"
        : twMerge(
            "rounded-md border-0 px-3 py-1.5 text-sm shadow-xs ring-1 ring-inset focus-within:ring-2 focus-within:ring-inset",
            ringColor,
            wrapperFocusStyle,
          ),
      textColor,
      placeholderColor,
      label ? "mt-2" : "",
      leftIcon ? "pl-8" : "",
      getBackgroundColor(),
      fillHeight ? "flex min-h-0 flex-1 flex-col" : "",
      className,
    );

    // Handle key down to potentially prevent new lines
    const handleKeyDown = (
      event: React.KeyboardEvent<HTMLTextAreaElement>,
    ): void => {
      if (shouldDisableNewline && event.key === "Enter") {
        event.preventDefault();
      }
      if (onKeyDown) {
        onKeyDown(event);
      }
    };

    return (
      <>
        <div className="flex items-center space-x-2">
          {label ? <Label size={Size.SMALL}>{label}</Label> : null}
          {labelAccessory}
        </div>
        <div className={textareaWrapperClassNames}>
          {leftIcon ? (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
              {leftIcon}
            </div>
          ) : null}
          <div
            ref={textareaInnerWrapperRef}
            className={fillHeight ? "flex min-h-0 flex-1 flex-col" : undefined}
          >
            <textarea
              aria-describedby={descriptionText ? descriptionId : undefined}
              aria-invalid={hasError}
              autoFocus={autoFocus}
              onFocus={onFocus}
              className={twMerge(
                "block w-full resize-none text-sm leading-6",
                variant === TextareaVariant.MINIMAL
                  ? "border-none p-0 outline-hidden focus:ring-0 focus:outline-hidden"
                  : "border-0 p-0 focus:ring-0",
                textColor,
                placeholderColor,
                isDisabled ? "bg-slate-50" : "",
                fillHeight ? "min-h-0 flex-1 overflow-y-auto" : "",
                props.textareaClassName,
              )}
              disabled={isDisabled}
              id={id}
              maxLength={maxLength || 10000}
              minLength={minLength || 0}
              onBlur={onBlur}
              onChange={(event) => onChange(event.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={onPaste}
              placeholder={placeholder}
              ref={textareaRef}
              rows={1}
              style={fillHeight ? undefined : { height: height || "auto" }}
              value={value}
            />
          </div>
        </div>
        {errorDescription ? (
          <FieldError className="mt-2" id={descriptionId}>
            {errorDescription}
          </FieldError>
        ) : description ? (
          <FieldDescription className="mt-2" id={descriptionId}>
            {description}
          </FieldDescription>
        ) : null}
      </>
    );
  },
);
